import useSportsnewBetPlacement from './useSportsnewBetPlacement';
import useSportsAccountBalance from './useSportsAccountBalance';
import { useContext, useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';

import { SportsBetCartContext } from '../../../_context/SportsBetCartContext';
import { SportsContext } from '../../../_context/SportsContext';
import SportsnewContext from '../../../_context/SportsnewContext';
import { useWalletSummary } from '../../../_context/wallet.context';

import useServiceContextHook from '../../../_hooks/service.context.hook';
import { notify } from '../../../_helpers/notify';

import { computeBetCartMaxStake } from '../_helpers/betCartStakeLimits';

import { sportsnewPlacementAttemptRuntime } from '../_helpers/sportsnewPlacementAttemptRuntime';
import {
  reconcileSportsnewPlacementAttempts,
} from '../_helpers/sportsnewPlacementAttemptResolution';

import useBetQuotePreview from '../_hooks/useBetQuotePreview';

import { sportsnewPreviewErrorMessage } from '../_helpers/sportsnewPreviewPlacementGate';

import { summarizeSportsBetCartChanges } from '../../../_context/_helpers/sportsBetCartBulkAcceptance';
import {
  captureSportsnewPlacementPresentationGuard,
} from '../_helpers/sportsnewPlacementAccountIdentity.owner';

const DISPLAY_SCOPE_BY_CATEGORY = {
  DOMESTIC: 'domestic',
  INTERNATIONAL: 'overseas',
  SPECIAL: 'esports',
  LIVE: 'live',
};
const fmt = n => Number(n).toLocaleString('ko-KR');
let bulkAcceptanceRequestSequence = 0;
const nextBulkAcceptanceRequestId = (ownerTag, revision) => {
  bulkAcceptanceRequestSequence += 1;
  return [
    'sportsnew-cart-accept',
    String(ownerTag || ''),
    String(revision),
    Date.now().toString(36),
    bulkAcceptanceRequestSequence.toString(36),
  ].join(':').slice(0, 160);
};
const accountKeyOf = guard => `${String(guard?.userId ?? '')}:${String(guard?.generation ?? '')}`;


/** Connects cart identity, quote and balance reads to the branded cart view. */
const useFigmaBetCartModel = ({
  isMobile = false,
  mobileOpen = false,
  presentationActive = true,
  onMobileClose = () => {},
  onMobilePresentationBusyChange,
}) => {
  const {
    selections, stake, setStake, removeSelection, clearCart, replaceSelectionsForType,
    totalOdds, currentType, setCurrentType,
    updateOddsInCart, oddsChanges, setBonusMultiplier,
    cartRevision, cartApplyReceipt, consumeCartApplyReceipt, acceptCartChanges,
    accountIdentity: currentAccountIdentity,
    captureAccountIdentity,
    isAccountIdentityCurrent,
  } = useContext(SportsBetCartContext);
  const { bettingType, displaySettings, forceResyncOdds } = useContext(SportsContext);
  const {
    displayScope,
    recoverSelectedOddsForCart,
  } = useContext(SportsnewContext);
  const { applyWalletSummary, walletSummary } = useWalletSummary();
  const _fmt = displaySettings?.default_odds_format || 'decimal';
  const { session, services: { SportsnewService } } = useServiceContextHook();
  const sessionUserId = session?.user?.id;
  const sessionToken = session?.user?.token;
  const activeCategory = currentType || bettingType || 'DOMESTIC';
  const activeDisplayScope = displayScope || DISPLAY_SCOPE_BY_CATEGORY[activeCategory] || 'domestic';
  const betMode = activeCategory === 'LIVE' ? 'live' : 'prematch';
  const betDetailService = SportsnewService;
  const isMountedRef = useRef(true);
  const currentAccountKey = currentAccountIdentity.accountKey;
  const captureAccountGuard = captureAccountIdentity;
  const isAccountGuardCurrent = useCallback((guard) => (
    isMountedRef.current
    && isAccountIdentityCurrent(guard)
  ), [isAccountIdentityCurrent]);
  const capturePlacementGuard = useCallback((attempt, phase) => (
    captureSportsnewPlacementPresentationGuard({
      attempt,
      identity: currentAccountIdentity,
      phase,
    })
  ), [currentAccountIdentity]);
  const isPlacementGuardCurrent = useCallback((guard, payload) => (
    isAccountGuardCurrent(guard)
    && guard?.surface === 'SPORTSNEW_BET_CART'
    && typeof guard?.challenge === 'string'
    && guard.challenge.length > 0
    && (!payload || guard.idempotencyKey === payload.idempotencyKey)
  ), [isAccountGuardCurrent]);
  const capturePlacementAuthSessionCarrier = useCallback((accountGuard) => (
    SportsnewService?.capturePlacementAuthSessionCarrier?.({
      accountGuard,
      isCurrent: () => isAccountGuardCurrent(accountGuard),
    })
  ), [SportsnewService, isAccountGuardCurrent]);
  const {
    availableBalance, balanceLoading, setBalanceLoading, setResolvedBalanceRecord,
    refreshAvailableBalance, applyAccountWallet,
  } = useSportsAccountBalance({
    currentAccountKey, captureAccountGuard, isAccountGuardCurrent, applyWalletSummary, walletSummary,
  });

  const [bettingLimits, setBettingLimits] = useState(null);
  const [bettingLimitsLoading, setBettingLimitsLoading] = useState(false);

  const betType = selections.length <= 1 ? 'SINGLE' : 'COMBO';
  const cartChangeSummary = useMemo(
    () => summarizeSportsBetCartChanges(selections),
    [selections],
  );
  useEffect(() => { setBonusMultiplier?.(1); }, [setBonusMultiplier]);
  useEffect(() => { setCurrentType(bettingType); }, [bettingType, setCurrentType]);
  const sportsnewServiceReady = Boolean(SportsnewService);
  useEffect(() => {
    if (!sportsnewServiceReady) return undefined;
    let cancelled = false;
    setBettingLimitsLoading(true);
    SportsnewService.getBettingLimits({
      betting_category: bettingType || 'DOMESTIC',
      bet_type: betType,
      scope: activeDisplayScope,
      displayScope: activeDisplayScope,
    }).then(res => {
      if (!cancelled) setBettingLimits(res?.data || null);
    }).catch(() => {
      if (!cancelled) setBettingLimits(null);
    }).finally(() => {
      if (!cancelled) setBettingLimitsLoading(false);
    });
    return () => { cancelled = true; };
  }, [sportsnewServiceReady, SportsnewService, bettingType, betType, activeDisplayScope, currentAccountKey]);

  useEffect(() => {
    if (!sessionUserId) {
      setResolvedBalanceRecord(null);
      return undefined;
    }
    // A fresh provider first renders an anonymous lifecycle identity. Do not
    // start a stale RESOLUTION flight that the committed identity would join.
    if (currentAccountIdentity.userId !== Number(sessionUserId)) return undefined;
    let cancelled = false;
    const userId = Number(sessionUserId);
    const reconcile = async () => {
      let attempts;
      try {
        attempts = sportsnewPlacementAttemptRuntime.list(userId);
      } catch {
        return;
      }
      if (attempts.length === 0) {
        await refreshAvailableBalance();
        return;
      }
      await reconcileSportsnewPlacementAttempts({
        attempts,
        runtime: sportsnewPlacementAttemptRuntime,
        userId,
        captureGuard: attempt => capturePlacementGuard(attempt, 'RESOLUTION'),
        resolveAttempt: (attempt, attemptGuard) => {
          const authSessionCarrier = capturePlacementAuthSessionCarrier(attemptGuard);
          return SportsnewService.resolvePlacementAttempt(attempt, { authSessionCarrier });
        },
        isGuardCurrent: isPlacementGuardCurrent,
        refreshWallet: refreshAvailableBalance,
        isCancelled: () => cancelled,
      });
    };
    reconcile().catch(() => {});
    return () => { cancelled = true; };
  }, [
    SportsnewService,
    capturePlacementAuthSessionCarrier,
    capturePlacementGuard,
    currentAccountIdentity.userId,
    isPlacementGuardCurrent,
    sessionUserId,
    sessionToken,
    refreshAvailableBalance,
    setResolvedBalanceRecord,
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (updateOddsInCart) {
      window.dispatchEvent(new CustomEvent('sp_cart_sync', { detail: { updateOddsInCart } }));
    }
  }, [updateOddsInCart]);

  const {
    displayedBaseOdds,
    displayedBonusMultiplier,
    comboOddsAdjust,
    displayedFinalOdds,
    displayedPotentialWin,
    bonusEligibleLegCount,
    bonusTierLegCount,
    quoteLoading,
    quoteError,
    quoteErrorCode,
    previewKey,
    authoritativeMinimumStake,
    hasAuthoritativeSportsnewQuote,
  } = useBetQuotePreview({
    SportsService: SportsnewService,
    selections,
    betType,
    bettingCategory: activeCategory,
    displayScope: activeDisplayScope,
    betMode,
    isSportsnew: true,
    requestOwnerKey: currentAccountKey,
    stake,
    totalOdds,
    bonusMultiplier: 1,
    bettingLimits,
    isAuthenticated: !!session?.user,
  });
  const maxStakeInfo = useMemo(() => computeBetCartMaxStake({
    balance: availableBalance,
    betType,
    bettingLimits,
    displayedFinalOdds,
  }), [availableBalance, betType, bettingLimits, displayedFinalOdds]);
  const quoteErrorMessage = useMemo(
    () => sportsnewPreviewErrorMessage(quoteError || quoteErrorCode),
    [quoteError, quoteErrorCode],
  );
  const effectiveMinimumStake = Math.max(
    Number(maxStakeInfo.limits.global_min_bet || 0),
    Number(authoritativeMinimumStake || 0),
  );

  const { submitting, pendingBetState, showBonusRecoveryModal, showBetSuccessModal, closeBetSuccessModal, bonusActionType, bonusGames, submissionGateRef, closeBonusRecoveryModal, handleBonusRecoveryConfirm, handlePlace } = useSportsnewBetPlacement({
    SportsnewService,
    activeCategory,
    activeDisplayScope,
    applyAccountWallet,
    betDetailService,
    betMode,
    betType,
    bettingLimits,
    bettingLimitsLoading,
    captureAccountGuard,
    capturePlacementAuthSessionCarrier,
    capturePlacementGuard,
    clearCart,
    currentAccountIdentity,
    currentAccountKey,
    displayedFinalOdds,
    effectiveMinimumStake,
    forceResyncOdds,
    hasAuthoritativeSportsnewQuote,
    isAccountGuardCurrent,
    isMountedRef,
    isPlacementGuardCurrent,
    maxStakeInfo,
    onMobileClose,
    presentationActive,
    previewKey,
    quoteError,
    quoteErrorCode,
    quoteLoading,
    recoverSelectedOddsForCart,
    refreshAvailableBalance,
    replaceSelectionsForType,
    selections,
    session,
    setBalanceLoading,
    setCurrentType,
    setResolvedBalanceRecord,
    setStake,
    stake,
  });

  // Presentation reports state; financial ownership stays inside the cart.
  // Ordinary quote reads are intentionally not a busy/close barrier.
  const presentationBusy = submitting || Boolean(pendingBetState) || showBonusRecoveryModal || showBetSuccessModal;
  const mobileHeadingRef = useRef(null);
  useLayoutEffect(() => {
    onMobilePresentationBusyChange?.(presentationBusy);
  }, [onMobilePresentationBusyChange, presentationBusy]);
  useLayoutEffect(() => {
    if (presentationActive && isMobile && mobileOpen) mobileHeadingRef.current?.focus({ preventScroll: true });
  }, [isMobile, mobileOpen, presentationActive]);
  const requestMobileClose = () => {
    if (presentationBusy || submissionGateRef.current.isLocked(captureAccountGuard())) return;
    onMobileClose();
  };

  const handleMaxStake = async () => {
    if (!session?.user) {
      notify('로그인이 필요합니다', 'error');
      return;
    }
    if (bettingLimitsLoading || balanceLoading) {
      notify('최대 가능 금액 계산 중입니다. 잠시 후 다시 시도해주세요', 'error');
      return;
    }
    const accountGuard = captureAccountGuard();
    if (!isAccountGuardCurrent(accountGuard)) return;
    let nextMaxStake = maxStakeInfo.maxStake;
    let nextMinStake = effectiveMinimumStake;
    if (nextMaxStake <= 0) {
      const freshBalance = await refreshAvailableBalance(accountGuard);
      if (!isAccountGuardCurrent(accountGuard)) return;
      const refreshed = computeBetCartMaxStake({
        balance: freshBalance,
        betType,
        bettingLimits,
        displayedFinalOdds,
      });
      nextMaxStake = refreshed.maxStake;
      nextMinStake = Math.max(
        Number(refreshed.limits.global_min_bet || 0),
        Number(authoritativeMinimumStake || 0),
      );
    }
    if (nextMaxStake <= 0) {
      notify('보유금이 없거나 잔액 정보를 아직 불러오지 못했습니다', 'error');
      return;
    }
    if (nextMaxStake < nextMinStake) {
      setStake('');
      notify(`현재 최대 가능 금액이 최소 베팅액 ${fmt(nextMinStake)}원보다 작습니다`, 'error');
      return;
    }
    setStake(String(nextMaxStake));
  };

  const handleAcceptCartChanges = useCallback(() => {
    const accountGuard = captureAccountGuard();
    if (
      !presentationActive
      || !isAccountGuardCurrent(accountGuard)
      || cartChangeSummary.actionableCount <= 0
    ) return;
    const ownerTag = accountKeyOf(accountGuard);
    const requestId = nextBulkAcceptanceRequestId(ownerTag, cartRevision);
    acceptCartChanges?.({
      requestId,
      ownerTag,
      expectedRevision: cartRevision,
    });
  }, [
    acceptCartChanges,
    captureAccountGuard,
    cartChangeSummary.actionableCount,
    cartRevision,
    isAccountGuardCurrent,
    presentationActive,
  ]);

  useEffect(() => {
    if (
      !presentationActive
      || !cartApplyReceipt
      || cartApplyReceipt.owner_tag !== currentAccountKey
      || cartApplyReceipt.committed_revision !== cartRevision
    ) return;
    const receipt = consumeCartApplyReceipt?.({
      requestId: cartApplyReceipt.request_id,
      ownerTag: cartApplyReceipt.owner_tag,
      committedRevision: cartApplyReceipt.committed_revision,
    });
    if (!receipt) return;
    const summary = receipt.summary || {};
    const messages = [];
    if (summary.changedCount > 0) messages.push(`변경 배당 ${summary.changedCount}개 반영`);
    if (summary.removedCount > 0) messages.push(`잠김/이용 불가 ${summary.removedCount}개 제외`);
    if (summary.unresolvedCount > 0) messages.push(`확인 필요 ${summary.unresolvedCount}개 유지`);
    if (messages.length > 0) notify(messages.join(' · '));
  }, [
    cartApplyReceipt,
    cartRevision,
    consumeCartApplyReceipt,
    currentAccountKey,
    presentationActive,
  ]);

  const handleIncrement = () => {
    setStake(p => {
      const current = Number(p) || 0;
      return String(current + 10000);
    });
  };

  const handleDecrement = () => {
    setStake(p => {
      const current = Number(p) || 0;
      return String(Math.max(0, current - 10000));
    });
  };

  const addAmt = v => v === 'max'
    ? handleMaxStake()
    : setStake(p => String((Number(p) || 0) + v));

  const comboAdjustText = comboOddsAdjust === 0
    ? '0.00'
    : `${comboOddsAdjust > 0 ? '+' : '-'}${Math.abs(comboOddsAdjust).toFixed(2)}`;
  const comboPreviewUnresolved = betType === 'COMBO'
    && (quoteLoading || !hasAuthoritativeSportsnewQuote);

  return {
    _fmt,
    addAmt,
    availableBalance,
    balanceLoading,
    bettingLimitsLoading,
    bonusActionType,
    bonusEligibleLegCount,
    bonusGames,
    bonusTierLegCount,
    cartChangeSummary,
    clearCart,
    closeBonusRecoveryModal,
    comboAdjustText,
    comboOddsAdjust,
    comboPreviewUnresolved,
    displayedBaseOdds,
    displayedBonusMultiplier,
    displayedFinalOdds,
    displayedPotentialWin,
    effectiveMinimumStake,
    handleAcceptCartChanges,
    handleBonusRecoveryConfirm,
    handleDecrement,
    handleIncrement,
    handlePlace,
    maxStakeInfo,
    mobileHeadingRef,
    oddsChanges,
    pendingBetState,
    presentationBusy,
    quoteErrorCode,
    quoteErrorMessage,
    quoteLoading,
    removeSelection,
    requestMobileClose,
    selections,
    setStake,
    showBonusRecoveryModal,
    showBetSuccessModal,
    closeBetSuccessModal,
    stake,
    submitting,
  };
};

export default useFigmaBetCartModel;
